const { vestResolver } = require('@hookform/resolvers/vest')

import { Anchor, Button, Flex, IconPlus, IconTrash } from '@vtex/admin-ui'

import { Select, TextInput, useFormState, useWatch } from '@vtex/admin-ui-form'
import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { LIMITS } from 'shared'
import { ClusterOperators, type Cluster } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../Drawer'
import styles from './cluster.css'
import { messages } from './messages'
import { validationSuite } from './validation'

type ClusterEditorProps = {
  onSave: (config: Cluster | undefined) => void
  values: Cluster | undefined
  state: UseDrawerStateReturn
}

const EMPTY_CONFIG = Object.freeze({
  operator: ClusterOperators.ALL,
  expressions: [''],
})

export function ClusterEditor(props: ClusterEditorProps) {
  const { values: initialValues, state: drawerState, onSave } = props
  const intl = useIntl()
  const { formatMessage: i18n } = intl

  const isCreating = initialValues === undefined

  const formValidationSuite = useMemo(() => validationSuite(intl), [intl])

  const subformState = useFormState<Cluster>({
    defaultValues: { ...EMPTY_CONFIG },
    resolver: vestResolver(formValidationSuite),
  })

  const expressions: string[] = useWatch({
    form: subformState,
    name: 'expressions',
  })

  const handleSave = async () => {
    const isValid = await subformState.trigger()

    if (!isValid) return

    const formValues = subformState.getValues()
    const hasFilledEntries = formValues.expressions.filter((x) => x).length
    onSave(hasFilledEntries ? formValues : undefined)

    drawerState.hide()
  }

  const handleAdd = (index?: number) => {
    const newEntries = [...expressions]
    const indexToAdd = index ?? newEntries.length
    newEntries.splice(indexToAdd, 0, '')

    subformState.setValue('expressions', newEntries)

    setTimeout(() => {
      document.getElementsByName(`expressions.${indexToAdd}`)[0]?.focus()
    })
  }

  const handleRemove = (index: number) => {
    const newEntries = [...expressions]
    newEntries.splice(index, 1)

    subformState.setValue('expressions', newEntries, {
      shouldValidate: true,
    })
  }

  const setInitialValues = useCallback(() => {
    const values = { ...EMPTY_CONFIG, ...initialValues }
    subformState.reset(values)
  }, [initialValues, subformState])

  useEffect(() => {
    drawerState.open && setInitialValues()
  }, [drawerState.open, setInitialValues])

  const showRemoveButton = expressions.length > 1

  return (
    <Drawer state={drawerState}>
      <DrawerHeader>
        <DrawerTitle>{i18n(messages.title)}</DrawerTitle>
        <DrawerSubtitle>
          {i18n(messages.subtitle, {
            a: (content: any) => (
              <Anchor target="_blank" href={i18n(messages.moreInformation)}>
                {content}
              </Anchor>
            ),
          })}
        </DrawerSubtitle>
        <DismissButton onHide={drawerState.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Flex direction="column" className={styles.content}>
          <Select
            name="operator"
            state={subformState}
            className={styles.operator}
            aria-label={i18n(messages.operator.ariaLabel)}
          >
            <option value={ClusterOperators.ALL}>
              {i18n(messages.operator.all)}
            </option>
            <option value={ClusterOperators.ANY}>
              {i18n(messages.operator.any)}
            </option>
          </Select>

          {expressions.map((_, index) => (
            <Flex
              key={index}
              direction="row"
              align="flex-start"
              className={styles.expression}
            >
              <span className={styles.input}>
                <TextInput
                  state={subformState}
                  name={`expressions.${index}`}
                  aria-label={i18n(messages.entryAriaLabel)}
                  onKeyDown={(event) =>
                    event.code === 'Enter' &&
                    event.currentTarget.value &&
                    handleAdd(index + 1)
                  }
                />
              </span>

              {showRemoveButton ? (
                <Button
                  className={styles.removeButton}
                  variant="neutralTertiary"
                  icon={<IconTrash />}
                  onClick={() => handleRemove(index)}
                  aria-label={i18n(messages.remove)}
                />
              ) : null}
            </Flex>
          ))}

          {expressions.length < LIMITS.MAX_CLUSTERS && (
            <Button
              aria-label={i18n(messages.add)}
              data-testid="add-entry"
              variant="neutralTertiary"
              icon={<IconPlus />}
              onClick={() => handleAdd()}
              className={styles.addButton}
            />
          )}
        </Flex>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={drawerState.hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
